import { SyntheticEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, Location } from "react-router-dom";

import { SlideAdmin } from "pwapi";

import { fetchAdminSlides } from "shared/actions/admin/adminSlidesActions";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { ControlledToggleButton } from "components/customInputs/buttons/ToggleButton";
import DateRangeInput from "components/customInputs/DateRangeInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import {
  reduceFiltersForSearchParams,
  reduceFiltersToApi,
  resetFilters,
} from "components/filtersUtils";

import { FilterPageFunctionComponentProps, Filter } from "../types/adminTypes";

type SlidesFiltersProps = FilterPageFunctionComponentProps & {
  initialFilters: Filter;
};

const SlidesFilters = ({
  sortBy,
  pageSize,
  pageIndex,
  toggleAllRowsExpanded,
  setPage,
  initialFilters,
}: SlidesFiltersProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const mapInitialFilters = (filters: Filter, initialFilters: Filter) =>
    Object.keys(filters).reduce(
      (acc: Filter, value) => ({ ...acc, [value]: initialFilters?.[value] }),
      {}
    );

  const [slidesFilters, setSlidesFilters] = useState<any>(
    mapInitialFilters(
      {
        accessionNumber: "",
        labName: "",
        status: "",
        clinicName: "",
        expectedUploadByAfter: "",
        expectedUploadByBefore: "",
      },
      initialFilters
    )
  );

  const [isLooseQuery, setIsLooseQuery] = useState<boolean>(
    initialFilters?.isLoose === "true"
  );

  useEffect(() => {
    filterSlides(slidesFilters, isLooseQuery);
  }, [sortBy, pageSize, pageIndex]);

  useEffect(() => {
    setSearchParams(location, slidesFilters, isLooseQuery);
  }, [sortBy, pageIndex]);

  const onChangeFilter = (key: string, value: string) => {
    setSlidesFilters({ ...slidesFilters, [key]: value });
  };

  const onChangeDateRangeFilter = (values: { [key: string]: string }) => {
    setSlidesFilters(Object.assign({}, slidesFilters, values));
  };

  const setSearchParams = (
    location: Location,
    filters: Filter,
    isLoose: boolean
  ) => {
    const params = {
      ...reduceFiltersForSearchParams(filters),
      ...(isLoose && { isLoose: "true" }),
    } as any;
    if (pageIndex > 0) {
      params.page = pageIndex + 1;
    }
    if (sortBy.length) {
      params.sortBy = sortBy.map((s) => `${s.desc ? "-" : ""}${s.id}`);
    }
    navigate(`${location.pathname}?${new URLSearchParams(params).toString()}`, {
      replace: true,
    });
  };

  const filterSlides = (filters: Filter, isLoose: boolean) => {
    dispatch(
      fetchAdminSlides({
        limit: pageSize,
        offset: pageSize * pageIndex,
        ordering: [...sortBy.map((s) => `${s.desc ? "-" : ""}${s.id}`)].join(
          ","
        ),
        ...reduceFiltersToApi(filters),
        ...(isLoose ? { isLoose: "true" } : {}),
      })
    );
  };

  const onSubmitFilter = (e: SyntheticEvent) => {
    e.preventDefault();
    setSearchParams(location, slidesFilters, isLooseQuery);
    filterSlides(slidesFilters, isLooseQuery);
    toggleAllRowsExpanded && toggleAllRowsExpanded(false);
    setPage(0);
  };

  const onResetFilter = () => {
    const newFilters = resetFilters(slidesFilters);
    setSlidesFilters(newFilters);
    setIsLooseQuery(false);
    setSearchParams(location, newFilters, false);
    filterSlides(newFilters, false);
    toggleAllRowsExpanded && toggleAllRowsExpanded(false);
    setPage(0);
  };

  return (
    <form className="AdminFilters" onSubmit={onSubmitFilter}>
      <div className="AdminFilters AdminFilters--vertical">
        <div className="AdminFilters__filters">
          <TextInput
            id="accessionNumber"
            value={slidesFilters.accessionNumber}
            label="Slide Accession"
            onChange={(e: any) =>
              onChangeFilter("accessionNumber", e.target.value)
            }
            autoComplete="off"
          />
          <SelectInput
            id="status"
            label="status"
            defaultLabel="All Statuses"
            options={Object.values(SlideAdmin.status).map((v) => ({
              label: v,
              value: v,
            }))}
            value={slidesFilters.status}
            onChange={(e: any) =>
              onChangeFilter("status", e.target.value || "")
            }
          />
          <TextInput
            id="labName"
            value={slidesFilters.labName}
            label="Lab"
            onChange={(e: any) => onChangeFilter("labName", e.target.value)}
          />
          <DateRangeInput
            id="expectedUploadDate"
            label="Expected Upload Date"
            values={{
              from: {
                id: "expectedUploadByAfter",
                name: "expected_upload_by_after",
                value: slidesFilters.expectedUploadByAfter,
              },
              to: {
                id: "expectedUploadByBefore",
                name: "expected_upload_by_before",
                value: slidesFilters.expectedUploadByBefore,
              },
            }}
            onChangeDate={onChangeDateRangeFilter}
          />
        </div>
      </div>
      <div className="AdminFilters__actions AdminFilters__actions--content">
        <ControlledToggleButton
          isToggled={isLooseQuery}
          title="toggle loose slides query"
          colors={{ on: "blue" }}
          onClick={() => {
            setIsLooseQuery(!isLooseQuery);
            setSearchParams(location, slidesFilters, !isLooseQuery);
            filterSlides(slidesFilters, !isLooseQuery);
          }}
        >
          Loose Slides
        </ControlledToggleButton>
        <div className="AdminFilters__actions--content">
          <PathologyWatchButton
            type="submit"
            color="green"
            title="filter slides"
          >
            Filter Slides
          </PathologyWatchButton>
          <PathologyWatchButton onClick={onResetFilter} title="reset filters">
            Reset Filters
          </PathologyWatchButton>
        </div>
      </div>
    </form>
  );
};

export default SlidesFilters;
